import { getFirestore, collection, doc, addDoc, setDoc, updateDoc, deleteDoc, onSnapshot, query, where, orderBy, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../firebase.js';

export const firebaseDatabaseService = {
  /**
   * Listen to real-time task updates for a user
   */
  onTasksChanged: (userId, callback, dateFilter = null) => {
    let tasksQuery = query(
      collection(db, 'users', userId, 'tasks'),
      orderBy('startTime', 'asc')
    );

    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const tasks = [];
      snapshot.forEach((doc) => {
        const taskData = doc.data();
        tasks.push({
          id: doc.id,
          ...taskData,
          date: taskData.date?.toDate() || new Date(),
          createdAt: taskData.createdAt?.toDate() || new Date(),
          updatedAt: taskData.updatedAt?.toDate() || new Date(),
          dateString: taskData.dateString || (taskData.date?.toDate() ?
            new Date(taskData.date.toDate().getTime() - (taskData.date.toDate().getTimezoneOffset() * 60000)).toISOString().split('T')[0] :
            null),
        });
      });

      const filteredTasks = dateFilter
        ? tasks.filter(task => task.dateString === dateFilter)
        : tasks;

      callback(filteredTasks);
    }, (error) => {
      console.error('Error fetching tasks:', error);
      callback([]);
    });

    return unsubscribe;
  },

  /**
   * Add a new task to the database
   */
  addTask: async (userId, taskData) => {
    try {
      const taskToSave = {
        ...taskData,
        date: new Date(taskData.date),
        createdAt: new Date(),
        updatedAt: new Date(),
        kind: taskData.kind || 'task',
        completed: taskData.completed || false,
        allDay: taskData.allDay || false,
        repeat: taskData.repeat || 'none',
        hasSubtask: taskData.hasSubtask || false,
        notes: taskData.notes || '',
        subtasks: taskData.subtasks || [],
        dateString: new Date(new Date(taskData.date).getTime() - (new Date(taskData.date).getTimezoneOffset() * 60000)).toISOString().split('T')[0],
      };

      const docRef = await addDoc(collection(db, 'users', userId, 'tasks'), taskToSave);

      return {
        success: true,
        task: {
          id: docRef.id,
          ...taskToSave,
        },
      };
    } catch (error) {
      console.error('Error adding task:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Failed to add task',
          code: error.code || 'unknown-error',
        },
      };
    }
  },

  /**
   * Update an existing task
   */
  updateTask: async (userId, taskId, updates) => {
    try {
      const taskRef = doc(db, 'users', userId, 'tasks', taskId);

      const updatesToSave = {
        ...updates,
        updatedAt: new Date(),
        ...(updates.date && { dateString: new Date(updates.date).toISOString().split('T')[0] }),
        ...(updates.date && { date: new Date(updates.date) }),
      };

      await updateDoc(taskRef, updatesToSave);

      return {
        success: true,
        task: {
          id: taskId,
          ...updatesToSave,
          ...updates,
          updatedAt: updatesToSave.updatedAt,
        },
      };
    } catch (error) {
      console.error('Error updating task:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Failed to update task',
          code: error.code || 'unknown-error',
        },
      };
    }
  },

  /**
   * Delete a task
   */
  deleteTask: async (userId, taskId) => {
    try {
      const taskRef = doc(db, 'users', userId, 'tasks', taskId);
      await deleteDoc(taskRef);

      return { success: true };
    } catch (error) {
      console.error('Error deleting task:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Failed to delete task',
          code: error.code || 'unknown-error',
        },
      };
    }
  },

  /**
   * Get all tasks for a user (one-time fetch)
   */
  getAllTasks: async (userId) => {
    try {
      const tasksQuery = query(
        collection(db, 'users', userId, 'tasks'),
        orderBy('startTime', 'asc')
      );

      const snapshot = await getDocs(tasksQuery);
      const tasks = [];

      snapshot.forEach((doc) => {
        const taskData = doc.data();
        tasks.push({
          id: doc.id,
          ...taskData,
          date: taskData.date?.toDate() || new Date(),
          createdAt: taskData.createdAt?.toDate() || new Date(),
          updatedAt: taskData.updatedAt?.toDate() || new Date(),
        });
      });

      return tasks;
    } catch (error) {
      console.error('Error getting all tasks:', error);
      return [];
    }
  },
};

// ─── Share Token CRUD ────────────────────────────────────────────────────────

/**
 * Create a share token for a user.
 * Token is used as the Firestore document ID — direct getDoc lookup, no index needed.
 */
export async function createShareToken(userId, label = 'Shared schedule') {
  const token = crypto.randomUUID();
  await setDoc(doc(db, 'shareTokens', token), {
    token,
    ownerUid: userId,
    createdAt: new Date(),
    label,
    active: true,
  });
  // Mark user as having sharing enabled (used by security rules)
  await setDoc(doc(db, 'userShareSettings', userId), { sharingEnabled: true }, { merge: true });
  return token;
}

/**
 * List all active share tokens for a user.
 * Queries by ownerUid only (single-field, auto-indexed) and filters active client-side.
 */
export async function listShareTokens(userId) {
  const q = query(collection(db, 'shareTokens'), where('ownerUid', '==', userId));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(t => t.active);
}

/**
 * Revoke (deactivate) a share token by document ID.
 * Disables sharing on userShareSettings if no active tokens remain.
 */
export async function revokeShareToken(docId) {
  const tokenRef = doc(db, 'shareTokens', docId);
  const snap = await getDoc(tokenRef);
  const { ownerUid } = snap.data() || {};
  await updateDoc(tokenRef, { active: false });
  if (ownerUid) {
    const remaining = await listShareTokens(ownerUid);
    if (remaining.length === 0) {
      await setDoc(doc(db, 'userShareSettings', ownerUid), { sharingEnabled: false }, { merge: true });
    }
  }
}

/**
 * Resolve a share token string → { id, ownerUid, active, label }
 * Uses direct document lookup by ID — no query, no index needed.
 * Returns null if not found.
 */
export async function resolveShareToken(token) {
  const snap = await getDoc(doc(db, 'shareTokens', token));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Fetch tasks for an ownerUid scoped to a dateString, for use in shared view.
 * No orderBy — sorted client-side to avoid requiring a composite index.
 */
export async function getTasksForSharedView(ownerUid, dateString) {
  const q = query(
    collection(db, 'users', ownerUid, 'tasks'),
    where('dateString', '==', dateString)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => {
      const data = d.data();
      return { id: d.id, ...data, date: data.date?.toDate() || new Date() };
    })
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
}
